
import { Point, EnemyType, TowerType, TowerBranch, TowerStats, EnemyStats } from '../types';
// Fixed: Removed non-existent LEVEL_PATH import from constants.ts
import { TOWER_STATS, ENEMY_STATS, MAGE_SLOW_FACTOR, MAGE_SLOW_DURATION, ARTILLERY_SPLASH_RADIUS } from '../constants';

export class Particle {
  x: number; y: number; vx: number; vy: number; life: number; color: string; size: number;
  constructor(x: number, y: number, color: string) {
    this.x = x; this.y = y; this.color = color;
    this.vx = (Math.random() - 0.5) * 4;
    this.vy = (Math.random() - 0.5) * 4;
    this.life = 1.0;
    this.size = Math.random() * 4 + 2;
  }
  update() { this.x += this.vx; this.y += this.vy; this.life -= 0.02; }
  draw(ctx: CanvasRenderingContext2D) {
    ctx.globalAlpha = this.life;
    ctx.fillStyle = this.color;
    ctx.beginPath(); ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
  }
}

export class Enemy {
  id: string; type: EnemyType; x: number; y: number; health: number; maxHealth: number;
  speed: number; reward: number; color: string; size: number; pathIndex: number = 0;
  distanceTraveled: number = 0; isBlocked: boolean = false; isDead: boolean = false;
  finished: boolean = false; bob: number = Math.random() * Math.PI;
  flashTime: number = 0; slowFactor: number = 1.0; slowTimer: number = 0;
  path: Point[];

  constructor(type: EnemyType, waveMultiplier: number = 1, path: Point[]) {
    this.id = Math.random().toString(36).substr(2, 9);
    this.type = type;
    this.path = path;
    const stats = ENEMY_STATS[type];
    this.maxHealth = stats.health * waveMultiplier;
    this.health = this.maxHealth;
    this.speed = stats.speed;
    this.reward = stats.reward;
    this.color = stats.color;
    this.size = stats.size;
    this.x = path[0].x;
    this.y = path[0].y;
  }

  update(gameSpeed: number) {
    if (this.isDead) return;
    if (this.slowTimer > 0) {
      this.slowTimer -= (16 * gameSpeed);
      if (this.slowTimer <= 0) this.slowFactor = 1.0;
    }
    if (this.isBlocked) return;
    const effectiveSpeed = this.speed * gameSpeed * this.slowFactor;
    this.bob += 0.1 * gameSpeed;
    const target = this.path[this.pathIndex + 1];
    if (!target) { this.finished = true; return; }
    const dx = target.x - this.x; const dy = target.y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < effectiveSpeed) {
      this.x = target.x; this.y = target.y; this.pathIndex++;
      if (this.pathIndex >= this.path.length - 1) this.finished = true;
    } else {
      this.x += (dx / dist) * effectiveSpeed; this.y += (dy / dist) * effectiveSpeed;
      this.distanceTraveled += effectiveSpeed;
    }
    if (this.flashTime > 0) this.flashTime--;
  }

  applySlow(factor: number, duration: number) { 
    this.slowFactor = Math.min(this.slowFactor, factor); 
    this.slowTimer = Math.max(this.slowTimer, duration); 
  }
  
  takeDamage(amount: number) { 
    this.health -= amount; 
    this.flashTime = 3; 
    if (this.health <= 0) this.isDead = true; 
  }

  draw(ctx: CanvasRenderingContext2D) {
    const bo = Math.sin(this.bob) * 3;
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.beginPath(); ctx.ellipse(this.x, this.y + this.size/2, this.size, this.size/3, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = this.flashTime > 0 ? '#fff' : (this.slowTimer > 0 ? '#00d2ff' : this.color);
    ctx.beginPath(); ctx.arc(this.x, this.y + bo, this.size, 0, Math.PI * 2); ctx.fill();
    const hbW = this.size * 2;
    ctx.fillStyle = '#333'; ctx.fillRect(this.x - hbW/2, this.y - this.size - 8 + bo, hbW, 4);
    ctx.fillStyle = this.slowTimer > 0 ? '#00d2ff' : '#4cd137';
    ctx.fillRect(this.x - hbW/2, this.y - this.size - 8 + bo, hbW * (this.health / this.maxHealth), 4);
  }
}

export class Soldier {
  x: number; y: number; bob: number = Math.random() * Math.PI; blockingEnemy: Enemy | null = null;
  constructor(x: number, y: number) { this.x = x; this.y = y; }
  update(gameSpeed: number) {
    this.bob += 0.1 * gameSpeed;
    if (this.blockingEnemy && (this.blockingEnemy.isDead || this.blockingEnemy.finished)) {
      this.blockingEnemy.isBlocked = false; this.blockingEnemy = null;
    }
  }
  draw(ctx: CanvasRenderingContext2D) {
    const bo = Math.sin(this.bob) * 2;
    ctx.fillStyle = '#4682B4'; ctx.beginPath(); ctx.roundRect(this.x - 7, this.y - 14 + bo, 14, 18, 3); ctx.fill();
  }
}

export class Tower {
  id: string; type: TowerType; x: number; y: number; level: number = 1;
  branch: TowerBranch = TowerBranch.NONE; lastShot: number = 0; soldiers: Soldier[] = [];

  constructor(type: TowerType, x: number, y: number) {
    this.id = Math.random().toString(36).substr(2, 9);
    this.type = type; this.x = x; this.y = y;
    if (type === TowerType.BARRACKS) this.createSoldiers();
  }

  get stats() {
    const base = TOWER_STATS[this.type];
    const lv = this.level - 1;
    let modRange = base.range + lv * 25;
    let modDamage = base.damage * (1 + lv * 0.6);
    let modCooldown = base.cooldown * (1 - lv * 0.1);
    if (this.branch === TowerBranch.PATH_A) { modDamage *= 2.2; modRange += 50; }
    if (this.branch === TowerBranch.PATH_B) { modCooldown *= 0.5; modRange += 20; }
    return { ...base, range: modRange, damage: modDamage, cooldown: modCooldown, upgradeCost: base.cost + (this.level * 50) };
  }

  upgrade(branch: TowerBranch = TowerBranch.NONE) {
    if (this.level < 4) this.level++;
    else if (this.level === 4) { this.branch = branch; this.level = 5; }
    if (this.type === TowerType.BARRACKS) this.createSoldiers();
  }

  createSoldiers() {
    const rp = { x: this.x, y: this.y + 60 };
    this.soldiers = [new Soldier(rp.x - 15, rp.y - 5), new Soldier(rp.x + 15, rp.y - 5), new Soldier(rp.x, rp.y + 12)];
  }

  draw(ctx: CanvasRenderingContext2D, isSelected: boolean) {
    const { range, color } = this.stats;
    if (isSelected) {
      ctx.beginPath(); ctx.arc(this.x, this.y, range, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)'; ctx.fill(); ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'; ctx.stroke();
    }
    ctx.fillStyle = '#555'; ctx.beginPath(); ctx.roundRect(this.x - 22, this.y - 35, 44, 45, 5); ctx.fill();
    ctx.fillStyle = this.branch !== TowerBranch.NONE ? '#ffd700' : color;
    ctx.beginPath(); ctx.moveTo(this.x - 24, this.y - 35); ctx.lineTo(this.x, this.y - 65 - (this.level * 4)); ctx.lineTo(this.x + 24, this.y - 35); ctx.fill();
    for(let i=0; i<this.level; i++) { ctx.fillStyle = '#ffd700'; ctx.beginPath(); ctx.arc(this.x-16+(i*8), this.y+18, 3, 0, Math.PI*2); ctx.fill(); }
  }
}

export class Projectile {
  x: number; y: number; startX: number; startY: number; targetX: number; targetY: number;
  target: Enemy | null; speed: number; damage: number; isExpired: boolean = false;
  color: string; type: TowerType; progress: number = 0;

  constructor(x: number, y: number, target: Enemy, damage: number, color: string, type: TowerType) {
    this.startX = x; this.startY = y; this.x = x; this.y = y;
    this.target = target; this.targetX = target.x; this.targetY = target.y;
    this.damage = damage; this.color = color; this.type = type;
    this.speed = type === TowerType.ARTILLERY ? 0.01 : 16;
  }

  update(gameSpeed: number, allEnemies: Enemy[], addParticles: (ps: Particle[]) => void) {
    if (this.type === TowerType.ARTILLERY) {
      this.progress += this.speed * gameSpeed;
      if (this.progress >= 1) {
        this.isExpired = true;
        const pts = []; for(let i=0; i<15; i++) pts.push(new Particle(this.targetX, this.targetY, '#f39c12')); addParticles(pts);
        allEnemies.forEach(e => {
          if (Math.sqrt((e.x - this.targetX)**2 + (e.y - this.targetY)**2) <= ARTILLERY_SPLASH_RADIUS) e.takeDamage(this.damage);
        });
      } else {
        this.x = this.startX + (this.targetX - this.startX) * this.progress;
        this.y = this.startY + (this.targetY - this.startY) * this.progress;
      }
    } else {
      if (this.target && !this.target.isDead) { this.targetX = this.target.x; this.targetY = this.target.y; }
      const dx = this.targetX - this.x; const dy = this.targetY - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const step = this.speed * gameSpeed;
      if (dist < step) {
        this.isExpired = true;
        if (this.target && !this.target.isDead) {
          this.target.takeDamage(this.damage);
          if (this.type === TowerType.MAGE) this.target.applySlow(MAGE_SLOW_FACTOR, MAGE_SLOW_DURATION);
        }
      } else {
        this.x += (dx / dist) * step; this.y += (dy / dist) * step;
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D) {
    let drawY = this.y;
    let size = 5;
    if (this.type === TowerType.ARTILLERY) {
      const h = 150; const offset = h * (1 - Math.pow(2 * this.progress - 1, 2));
      drawY -= offset; size = 8 + (offset / 10);
      ctx.fillStyle = 'rgba(0,0,0,0.2)'; ctx.beginPath(); ctx.arc(this.x, this.y, 6, 0, Math.PI * 2); ctx.fill();
    }
    ctx.fillStyle = this.color;
    ctx.beginPath(); ctx.arc(this.x, drawY, size, 0, Math.PI * 2); ctx.fill();
  }
}
